import { useCallback } from 'react';

import { useAIGeneratedCoverImage } from '@atlassian/ai-image-generation/use-ai-generated-cover-image';
import { useRemixImageGeneration } from '@atlassian/ai-image-generation/use-remix-image-generation';
import { IMAGE_GENERATION_THEMES } from '@atlassian/ai-image-generation/types';
import type {
	GenerateImageEventCallbacks,
	GenerateImageProps,
	ImageGenerationTheme,
	UseAIGeneratedCoverImageReturn,
} from '@atlassian/ai-image-generation/types';

import { createEditorMediaProvider } from '@confluence/fabric-media-support/entry-points/MediaProvider.ts';
import { fg } from '@confluence/feature-gating';

type UseHeaderImageGenerationProps = {
	cloudId: string;
	contentId: string;
	eventCallbacks?: GenerateImageEventCallbacks;
	userId: string | null;
};

const getRemixHeaderImageTheme = (theme: GenerateImageProps['theme']): ImageGenerationTheme => {
	switch (theme) {
		case IMAGE_GENERATION_THEMES.threeD:
			return IMAGE_GENERATION_THEMES.headerImagesThreeD;
		case IMAGE_GENERATION_THEMES.sketch:
			return IMAGE_GENERATION_THEMES.headerImagesSketch;
		case IMAGE_GENERATION_THEMES.watercolors:
			return IMAGE_GENERATION_THEMES.headerImagesWatercolors;
		default:
			return IMAGE_GENERATION_THEMES.headerImages;
	}
};

/**
 * Selects the header-image generation backend while preserving the cover picture lifecycle.
 *
 * Remix returns the copied media ID as `NOT_LOADING` so EditorPageCoverPicture can persist it and
 * complete either the manual acceptance flow or the AIFC auto-apply flow before marking it as set.
 */
export const useHeaderImageGeneration = ({
	cloudId,
	contentId,
	eventCallbacks,
	userId,
}: UseHeaderImageGenerationProps): UseAIGeneratedCoverImageReturn => {
	const legacyImageGeneration = useAIGeneratedCoverImage({
		contentId,
		eventCallbacks,
	});
	const remixImageGeneration = useRemixImageGeneration({
		cloudId,
		contentId,
		createEditorMediaProvider,
		eventCallbacks,
		successfulGenerationStatus: 'NOT_LOADING',
		touchpointSource: 'confluence-header-image-generation',
		userId,
	});
	const { generateImage: generateRemixImage } = remixImageGeneration;
	const generateRemixHeaderImage = useCallback(
		(props: GenerateImageProps) =>
			generateRemixImage({
				...props,
				theme: getRemixHeaderImageTheme(props.theme),
			}),
		[generateRemixImage],
	);

	if (fg('header_images_migrate_image_gen_to_remix')) {
		return {
			...remixImageGeneration,
			data: null,
			generateImage: generateRemixHeaderImage,
		};
	}

	return legacyImageGeneration;
};
